import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Button,
  Dropdown,
  Menu,
  Card,
  Input,
  Row,
  Col,
  Typography,
  Tooltip,
  message,
  Popconfirm,
  Modal,
} from "antd";
import {
  EyeOutlined,
  MailOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CalendarOutlined,
  MoreOutlined,
  SearchOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import {
  fetchDemoRequests,
  deleteDemoRequest,
} from "../../apis";
import "../../styles/admin/admin-dashboard.css";
import "../../styles/admin/admin-tables.css";
import "../../styles/admin/richiedi-demo.css";

const { Title } = Typography;
const { Search } = Input;

const RichiediDemo = () => {
  const [demoRequests, setDemoRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchDemoRequests();

      const formattedRequests = data.map((request, index) => ({
        key: index + 1,
        ...request,
        // Keep the original Date object for sorting
        created_at_date: new Date(request.created_at),
        // Formatted versions for display
        created_at_formatted: new Date(request.created_at).toLocaleDateString(
          "it-IT"
        ),
        created_at_time: new Date(request.created_at).toLocaleTimeString(
          "it-IT",
          {
            hour: "2-digit",
            minute: "2-digit",
          }
        ),
        created_at_full: new Date(request.created_at).toLocaleString("it-IT", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      }));

      setDemoRequests(formattedRequests);
      setFilteredRequests(formattedRequests);
    } catch (error) {
      console.error("Fetch error:", error);
      setError("Errore nel caricamento delle richieste demo");
      message.error("Errore nel caricamento delle richieste demo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    if (!value) {
      setFilteredRequests(demoRequests);
    } else {
      const filtered = demoRequests.filter((request) =>
        Object.values(request).some(
          (field) =>
            field &&
            field.toString().toLowerCase().includes(value.toLowerCase())
        )
      );
      setFilteredRequests(filtered);
    }
  };

  const handleDelete = async (record) => {
    try {
      await deleteDemoRequest(record.id);
      message.success("Richiesta demo eliminata con successo");
      fetchData();
    } catch (error) {
      message.error("Errore nell'eliminazione della richiesta demo");
    }
  };

  const showDetails = (record) => {
    setSelectedRequest(record);
    setDetailModalVisible(true);
  };

  // Function to handle contact action
  const handleContact = (record) => {
    const email = record.email;
    const subject = `Regarding your demo request: ${record.subject}`;
    const body = `Dear ${
      record.company || "Customer"
    },\n\nRegarding your demo request: "${record.subject}"\n\n`;

    // Create mailto link
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    // Open email client
    window.location.href = mailtoLink;
  };

  const actionMenu = (record) => (
    <Menu>
      <Menu.Item
        key="view"
        icon={<EyeOutlined />}
        onClick={() => showDetails(record)}
      >
        Visualizza Dettagli
      </Menu.Item>
      <Menu.Item
        key="contact"
        icon={<MailOutlined />}
        onClick={() => handleContact(record)}
      >
        Contatta
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="delete" danger icon={<DeleteOutlined />}>
        <Popconfirm
          title="Eliminazione richiesta demo"
          description="Sei sicuro di voler eliminare questa richiesta demo?"
          onConfirm={() => handleDelete(record)}
          okText="Conferma"
          cancelText="Annulla"
          okButtonProps={{
            className: "admin-form-button-primary",
          }}
        >
          <span className="table-action-delete">Elimina</span>
        </Popconfirm>
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span className="email-text">{text}</span>
        </Tooltip>
      ),
    },
    {
      title: "Azienda",
      dataIndex: "company",
      key: "company",
      width: 150,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Tag color="blue">{text}</Tag>
        </Tooltip>
      ),
    },
    {
      title: "Oggetto",
      dataIndex: "subject",
      key: "subject",
      width: 180,
      ellipsis: true,
      render: (text) => <span className="subject-text">{text}</span>,
    },
    {
      title: "Data e Ora Richiesta",
      dataIndex: "created_at_date",
      key: "created_at",
      width: 150,
      sorter: (a, b) => a.created_at_date - b.created_at_date,
      defaultSortOrder: "descend",
      render: (date, record) => (
        <div>
          <Tag
            icon={<CalendarOutlined />}
            color="default"
            className="date-tag"
          >
            {record.created_at_formatted}
          </Tag>
          <Tag icon={<ClockCircleOutlined />} color="default">
            {record.created_at_time}
          </Tag>
        </div>
      ),
    },
    {
      title: "Descrizione",
      dataIndex: "description",
      key: "description",
      width: 250,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span className="description-text">
            {text
              ? `${text.substring(0, 60)}${text.length > 60 ? "..." : ""}`
              : "Nessuna descrizione"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Azioni",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Dropdown
          overlay={actionMenu(record)}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            className="action-button"
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="richiedi-demo-container">
      <Modal
        title="Dettagli Richiesta Demo"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Chiudi
          </Button>,
        ]}
        width={600}
      >
        {selectedRequest && (
          <div className="modal-content">
            <p>
              <strong>Email:</strong> {selectedRequest.email}
            </p>
            <p>
              <strong>Azienda:</strong> {selectedRequest.company}
            </p>
            <p>
              <strong>Oggetto:</strong> {selectedRequest.subject}
            </p>
            <p>
              <strong>Data e Ora Richiesta:</strong>{" "}
              {selectedRequest.created_at_full}
            </p>
            <p>
              <strong>Descrizione:</strong>
            </p>
            <div className="description-box">
              {selectedRequest.description || "Nessuna descrizione"}
            </div>
          </div>
        )}
      </Modal>

      <Card className="demo-card">
        <Row className="card-header" justify="space-between" align="middle">
          <Col>
            <Title level={3} className="page-title">
              <MailOutlined className="title-icon" />
              Richieste Demo
            </Title>
            <p className="page-subtitle">
              Gestisci tutte le richieste demo ricevute
            </p>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={fetchData}
              loading={loading}
              className="refresh-button"
            >
              Aggiorna
            </Button>
          </Col>
        </Row>

        <Row gutter={[16, 16]} className="search-row">
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Cerca richieste..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              onChange={(e) => !e.target.value && handleSearch("")}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <div className="total-counter">
              <span>
                Totale: <strong>{filteredRequests.length}</strong> richieste
              </span>
            </div>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredRequests}
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} di ${total} richieste`,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          scroll={{ x: 1000 }}
          bordered
          size="middle"
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
        />
      </Card>
    </div>
  );
};

export default RichiediDemo;
